import React, { useState, useEffect, useRef } from "react";
import { Link, useLocation, useNavigate } from "react-router-dom";
import Logo from "./Logo";
import { useNavbar } from "../context/NavbarContext";
import { SECTORS } from "../features/landing/data/sectors";

const NAVBAR_HEIGHT = 80;

export default function Navbar() {
  const location = useLocation();
  const navigate = useNavigate();
  const [isOpen, setIsOpen] = useState(false);
  const [isScrolled, setIsScrolled] = useState(false);
  const [navHidden, setNavHidden] = useState(false);
  const { setNavbarHeight } = useNavbar();
  const [sectorDropdownOpen, setSectorDropdownOpen] = useState(false);
  const [mobileSectorOpen, setMobileSectorOpen] = useState(false);
  const sectorDropdownRef = useRef(null);

  const updatePagePadding = (height) => {
    window.dispatchEvent(new CustomEvent("navbarHeightChanged", { detail: { height } }));
  };

  // Dropdown dışına tıklanınca kapat
  useEffect(() => {
    const handler = (e) => {
      if (sectorDropdownRef.current && !sectorDropdownRef.current.contains(e.target)) {
        setSectorDropdownOpen(false);
      }
    };
    document.addEventListener("mousedown", handler);
    return () => document.removeEventListener("mousedown", handler);
  }, []);

  // Route değişince mobil menüyü kapat, navbar'ı göster
  useEffect(() => {
    setNavHidden(false);
    setIsOpen(false);
    setSectorDropdownOpen(false);
  }, [location.pathname]);

  // /isletmeler'de scroll > 0 iken navbar gizle
  useEffect(() => {
    if (!location.pathname.startsWith("/isletmeler")) return;

    const handleScroll = () => {
      setNavHidden((window.scrollY || 0) > 0);
    };
    handleScroll();
    window.addEventListener("scroll", handleScroll, { passive: true });
    return () => window.removeEventListener("scroll", handleScroll);
  }, [location.pathname]);

  // navHidden değişince içeriğin paddingTop'ını güncelle
  useEffect(() => {
    const height = navHidden ? 0 : NAVBAR_HEIGHT;
    setNavbarHeight(height);
    updatePagePadding(height);
  }, [navHidden, setNavbarHeight]);

  // Route değişince navbar yüksekliğini sıfırla
  useEffect(() => {
    setNavbarHeight(NAVBAR_HEIGHT);
    updatePagePadding(NAVBAR_HEIGHT);
  }, [setNavbarHeight, location.pathname]);

  // Scroll shadow
  useEffect(() => {
    const onScroll = () => setIsScrolled((window.scrollY || 0) > 8);
    onScroll();
    window.addEventListener("scroll", onScroll, { passive: true });
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  const routeSet = process.env.REACT_APP_ROUTE_SET || "tr";
  const isTR = routeSet !== "en";

  const paths = isTR
    ? { businesses: "/isletmeler", demo: "/demo-talebi", query: "/randevu-sorgula" }
    : { businesses: "/businesses", demo: "/demo-request", query: "/booking/search" };

  const isActive = (path) => location.pathname === path || location.pathname.startsWith(path + "/");

  const linkClass = (path) =>
    `text-sm font-medium transition-colors duration-200 ${
      isActive(path) ? "text-primary" : "text-gray-600 hover:text-gray-900"
    }`;

  return (
    <>
      <nav
        className="fixed w-full z-50 bg-white"
        style={{
          top: 0,
          transition: "transform 0.3s cubic-bezier(0.4, 0, 0.2, 1)",
          boxShadow: isScrolled ? "0 6px 18px -6px rgba(15, 23, 42, 0.12)" : "none",
          transform: navHidden ? `translateY(-${NAVBAR_HEIGHT}px)` : "translateY(0)",
        }}
      >
        <div className="max-w-7xl mx-auto flex items-center h-20 px-4 md:px-6 gap-8">
          {/* Logo */}
          <Link to="/" className="flex-shrink-0">
            <Logo />
          </Link>

          {/* Desktop: sol nav linkleri */}
          <div className="hidden md:flex items-center gap-6">
            <Link to={paths.businesses} className={linkClass(paths.businesses)}>
              {isTR ? "İşletmeler" : "Businesses"}
            </Link>

            {/* Sektörler dropdown */}
            <div className="relative" ref={sectorDropdownRef}>
              <button
                onClick={() => setSectorDropdownOpen((v) => !v)}
                className={`flex items-center gap-1 text-sm font-medium transition-colors duration-200 ${
                  sectorDropdownOpen ? "text-primary" : "text-gray-600 hover:text-gray-900"
                }`}
              >
                {isTR ? "Sektörler" : "Sectors"}
                <svg
                  className={`w-4 h-4 transition-transform duration-200 ${sectorDropdownOpen ? "rotate-180" : ""}`}
                  fill="none" viewBox="0 0 24 24" stroke="currentColor"
                >
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
                </svg>
              </button>

              {sectorDropdownOpen && (
                <div className="absolute top-full left-0 mt-2 w-60 bg-white rounded-xl shadow-lg border border-gray-100 py-1.5 z-50">
                  {SECTORS.map((sector) => (
                    <Link
                      key={sector.id}
                      to={`/${sector.slug}`}
                      onClick={() => setSectorDropdownOpen(false)}
                      className="flex items-center gap-3 px-4 py-2.5 hover:bg-gray-50 transition-colors"
                    >
                      <span className="text-base leading-none">{sector.icon}</span>
                      <span className="text-sm text-gray-700">{sector.title}</span>
                    </Link>
                  ))}
                </div>
              )}
            </div>
          </div>

          {/* Desktop: sağ taraf — spacer + iki aksiyon */}
          <div className="hidden md:flex items-center gap-3 ml-auto">
            <a
              href="https://panel.pinerandevu.com"
              target="_blank"
              rel="noopener noreferrer"
              className="px-4 py-2 text-sm font-medium text-gray-600 hover:text-gray-900 border border-gray-200 rounded-lg hover:border-gray-300 transition-colors"
            >
              {isTR ? "İşletme Girişi" : "Business Login"}
            </a>

            {/* Müşteri utilitesi — ghost */}
            <Link
              to={paths.query}
              className="px-4 py-2 text-sm font-medium text-gray-600 hover:text-gray-900 border border-gray-200 rounded-lg hover:border-gray-300 transition-colors"
            >
              {isTR ? "Randevu Sorgula" : "Search Booking"}
            </Link>

            {/* Satış CTA — primary */}
            <button
              onClick={() => navigate(paths.demo)}
              className="px-4 py-2 text-sm font-semibold text-white bg-primary rounded-lg hover:bg-primary/90 transition-colors"
            >
              {isTR ? "Ücretsiz Başla" : "Start Free"}
            </button>
          </div>

          {/* Mobile: hamburger */}
          <button
            onClick={() => setIsOpen((v) => !v)}
            className="md:hidden ml-auto p-2 rounded-md text-gray-600 hover:text-gray-900 hover:bg-gray-50 transition-colors"
            aria-label="Menüyü aç"
          >
            {isOpen ? (
              <svg className="h-6 w-6" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
              </svg>
            ) : (
              <svg className="h-6 w-6" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 6h16M4 12h16M4 18h16" />
              </svg>
            )}
          </button>
        </div>

        {/* Mobile menü */}
        {isOpen && (
          <div className="md:hidden border-t border-gray-100 bg-white px-4 py-3 space-y-1">
            <Link
              to={paths.businesses}
              onClick={() => setIsOpen(false)}
              className="block py-2.5 px-3 rounded-lg text-sm font-medium text-gray-700 hover:bg-gray-50 transition-colors"
            >
              {isTR ? "İşletmeler" : "Businesses"}
            </Link>

            {/* Sektörler accordion */}
            <div>
              <button
                onClick={() => setMobileSectorOpen((v) => !v)}
                className="w-full flex items-center justify-between py-2.5 px-3 rounded-lg text-sm font-medium text-gray-700 hover:bg-gray-50 transition-colors"
              >
                <span>{isTR ? "Sektörler" : "Sectors"}</span>
                <svg
                  className={`w-4 h-4 text-gray-400 transition-transform ${mobileSectorOpen ? "rotate-180" : ""}`}
                  fill="none" viewBox="0 0 24 24" stroke="currentColor"
                >
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
                </svg>
              </button>
              {mobileSectorOpen && (
                <div className="mt-1 ml-3 border-l border-gray-100 pl-3 space-y-0.5">
                  {SECTORS.map((sector) => (
                    <Link
                      key={sector.id}
                      to={`/${sector.slug}`}
                      onClick={() => { setIsOpen(false); setMobileSectorOpen(false); }}
                      className="flex items-center gap-3 py-2 px-2 rounded-lg text-sm text-gray-600 hover:bg-gray-50 transition-colors"
                    >
                      <span className="text-base leading-none">{sector.icon}</span>
                      <span>{sector.title}</span>
                    </Link>
                  ))}
                </div>
              )}
            </div>

            <Link
              to={paths.query}
              onClick={() => setIsOpen(false)}
              className="block py-2.5 px-3 rounded-lg text-sm font-medium text-gray-700 hover:bg-gray-50 transition-colors"
            >
              {isTR ? "Randevu Sorgula" : "Search Booking"}
            </Link>

            <a
              href="https://panel.pinerandevu.com"
              target="_blank"
              rel="noopener noreferrer"
              onClick={() => setIsOpen(false)}
              className="block py-2.5 px-3 rounded-lg text-sm font-medium text-gray-700 hover:bg-gray-50 transition-colors"
            >
              {isTR ? "İşletme Girişi" : "Business Login"}
            </a>

            {/* Mobile primary CTA */}
            <div className="pt-2 pb-1">
              <button
                onClick={() => { navigate(paths.demo); setIsOpen(false); }}
                className="w-full py-2.5 px-4 bg-primary text-white text-sm font-semibold rounded-xl hover:bg-primary/90 transition-colors"
              >
                {isTR ? "Ücretsiz Başla" : "Start Free"}
              </button>
            </div>
          </div>
        )}
      </nav>
    </>
  );
}
